import { createFetcherWithMiddlewares } from '../middleware';
import { createFetcherArgs, createFetcherResp, createMockedFetcher } from '../middleware.spec';
import { CsrfMiddleware, HttpTextResponse } from './csrf';

const createCsrfHttpClientResp = (resp: HttpTextResponse) => resp;

const createMockedCsrfHttpClient = (responseText: string | null) =>
    jest.fn().mockResolvedValue(
        createCsrfHttpClientResp({
            ok: responseText != null,
            text: async () => {
                if (responseText == null) throw new Error('Should fail');
                return responseText;
            },
        }),
    );

describe('CSRF Token Middleware', () => {
    it.each([['GET'], ['HEAD'], ['OPTIONS'], ['TRACE']])(
        'should NOT attach CSRF token to safe HTTP method "%s"',
        async (method) => {
            const fetcher = createMockedFetcher();
            const fetcherWithMiddleware = createFetcherWithMiddlewares(fetcher, [new CsrfMiddleware()]);

            await fetcherWithMiddleware({
                url: '/some/url',
                method,
            });

            expect(fetcher.mock.calls.length).toEqual(1);
            expect(fetcher.mock.lastCall[0]).toEqual(
                createFetcherArgs({
                    url: '/some/url',
                    method,
                    headers: {},
                }),
            );
        },
    );

    it.each([['POST'], ['PUT'], ['DELETE'], ['PATCH'], ['CONNECT']])(
        'should attach CSRF token to unsafe HTTP method "%s"',
        async (method) => {
            const fetcher = createMockedFetcher();
            const csrfHttpClient = createMockedCsrfHttpClient('123');

            const fetcherWithMiddleware = createFetcherWithMiddlewares(fetcher, [
                new CsrfMiddleware({
                    csrfHttpClient,
                }),
            ]);

            await fetcherWithMiddleware({
                url: '/some/url',
                method,
            });

            expect(fetcher.mock.calls.length).toEqual(1);
            expect(fetcher.mock.lastCall[0]).toEqual(
                createFetcherArgs({
                    url: '/some/url',
                    method,
                    headers: {
                        'X-CSRF-Token': '123',
                    },
                }),
            );

            expect(csrfHttpClient.mock.calls.length).toEqual(1);
            expect(csrfHttpClient.mock.lastCall[0]).toEqual('/auth/csrf');
        },
    );

    it('should request token only once', async () => {
        const fetcher = createMockedFetcher();
        const csrfHttpClient = createMockedCsrfHttpClient('123');

        const fetcherWithMiddleware = createFetcherWithMiddlewares(fetcher, [
            new CsrfMiddleware({
                csrfHttpClient,
            }),
        ]);

        await fetcherWithMiddleware({
            url: '/some/url',
            method: 'POST',
        });

        expect(csrfHttpClient.mock.calls.length).toEqual(1);

        await fetcherWithMiddleware({
            url: '/some/url',
            method: 'DELETE',
        });

        expect(csrfHttpClient.mock.calls.length).toEqual(1);
    });

    it('should use custom URL to issue CSRF token', async () => {
        const fetcher = createMockedFetcher();
        const csrfHttpClient = createMockedCsrfHttpClient('123');

        const fetcherWithMiddleware = createFetcherWithMiddlewares(fetcher, [
            new CsrfMiddleware({
                csrfHttpClient,
                csrfEndpoint: '/custom/csrf/endpoint',
            }),
        ]);

        await fetcherWithMiddleware({
            url: '/some/url',
            method: 'POST',
        });

        expect(csrfHttpClient.mock.lastCall[0]).toEqual('/custom/csrf/endpoint');
    });

    it('should attach X-External header if configured', async () => {
        const fetcher = createMockedFetcher();
        const fetcherWithMiddleware = createFetcherWithMiddlewares(fetcher, [
            new CsrfMiddleware({
                attachExternalHeaderToRequests: true,
            }),
        ]);

        await fetcherWithMiddleware({
            url: '/some/url',
            method: 'GET',
        });

        expect(fetcher.mock.calls.length).toEqual(1);
        expect(fetcher.mock.lastCall[0]).toEqual(
            createFetcherArgs({
                url: '/some/url',
                method: 'GET',
                headers: {
                    'X-External': 'true',
                },
            }),
        );
    });

    it('should throw an error on invalid csrf API call response', async () => {
        const fetcher = createMockedFetcher();
        const csrfHttpClient = createMockedCsrfHttpClient(null);

        const fetcherWithMiddleware = createFetcherWithMiddlewares(fetcher, [
            new CsrfMiddleware({
                csrfHttpClient,
            }),
        ]);

        await expect(
            fetcherWithMiddleware({
                url: '/some/url',
                method: 'POST',
            }),
        ).resolves.toMatchObject({
            ok: false,
            error: {
                reason: 'unknown',
                errorMessage: "Can't issue CSRF token",
            },
        });
    });
});
